import { createFileRoute } from "@tanstack/react-router";
import { PolicyLayout } from "@/components/PolicyLayout";
import { ConsumerRightsNotice } from "@/components/policies/ConsumerRightsNotice";
import { COD_ADVANCE_INR } from "@/lib/checkout-pricing";

export const Route = createFileRoute("/policies/shipping-policy")({
  component: ShippingPolicy,
  head: () => ({
    meta: [
      { title: "Shipping Policy. TruWe" },
      { name: "description", content: "Order processing times, shipping partners, delivery timelines and tracking for TruWe orders across India." },
    ],
    links: [{ rel: "canonical", href: "https://truwe.in/policies/shipping-policy" }],
  }),
});

function ShippingPolicy() {
  return (
    <PolicyLayout title="Shipping Policy" updated="4 October 2026">
      <p>
        This Shipping Policy explains how TruWe ("TruWe", "we", "us") processes,
        packs and dispatches orders placed on <a href="https://truwe.in">truwe.in</a>.
      </p>

      <ConsumerRightsNotice />
      <h2>1. Order processing</h2>
      <p>
        Orders are processed within <strong>24-48 business hours</strong> of payment confirmation
        (Monday to Saturday, excluding public holidays). You will receive an order confirmation
        email immediately after checkout, followed by a dispatch email with tracking details.
      </p>

      <h2>2. Shipping partners</h2>
      <p>
        We ship pan-India through trusted logistics partners including Shiprocket, Delhivery,
        Bluedart, DTDC and Indian Post. The partner is selected based on serviceability of your
        pincode.
      </p>

      <h2>3. Delivery timelines</h2>
      <ul>
        <li><strong>Metro cities:</strong> 2-4 business days from dispatch</li>
        <li><strong>Tier 2 / Tier 3 cities:</strong> 4-7 business days from dispatch</li>
        <li><strong>Remote / North-East / J&K / island regions:</strong> 7-10 business days</li>
      </ul>
      <p>
        Timelines are indicative. Occasional delays may occur due to weather, courier load,
        regional restrictions or unforeseen events.
      </p>

      <h2>4. Shipping charges</h2>
      <ul>
        <li><strong>Free shipping</strong> on all orders, with no minimum order value.</li>
        <li>Prepaid orders receive an additional 5% discount at checkout.</li>
        <li>Cash on Delivery (where available) needs a ₹{COD_ADVANCE_INR} advance paid online, deducted from your order total; the balance is paid on delivery. No COD fee.</li>
      </ul>

      <h2>5. Tracking your order</h2>
      <p>
        Once dispatched, you will receive a tracking link via email and SMS. You can track the
        live status of your shipment using the courier's tracking portal.
      </p>

      <h2>6. Non-delivery, RTO & wrong addresses</h2>
      <p>
        Check your address and contact details before ordering. The COD advance is part of
        the purchase price, not a fee or an automatically forfeitable deposit. Cancellation
        before dispatch or cancellation by TruWe receives a refund of the advance. For courier
        failure, loss or other non-delivery not caused by you, contact us for redelivery or a
        refund as appropriate. If delivery fails because of an incorrect address, unavailability
        or refusal, we will review the facts with you. Any lawful, previously disclosed deduction
        must be itemised; an RTO label alone does not authorise forfeiture. Reshipment requires
        your agreement to the delivery arrangement and any applicable disclosed cost.
      </p>

      <h2>7. Damaged or missing items</h2>
      <p>
        If your parcel arrives damaged, tampered with or with missing items, contact{" "}
        <a href="mailto:hello@truwe.in">hello@truwe.in</a> promptly, preferably within
        <strong> 3 days of delivery</strong>. Include your order ID and available photos.
        Video is optional. This reporting target does not exclude later-discovered defects,
        safety concerns or statutory rights. We assess replacement or refund under the
        <a href="/policies/refund-policy"> Refund Policy</a>.
      </p>

      <h2>8. International shipping</h2>
      <p>
        We currently ship only within India. International orders are not supported at this time.
      </p>

      <h2>9. Contact</h2>
      <p>
        For shipping questions, write to <a href="mailto:hello@truwe.in">hello@truwe.in</a> with
        your order ID.
      </p>
    </PolicyLayout>
  );
}
